/*
 * Copyright 2026 Sony Corporation
 */

import { SceneDocSchema, type SceneDoc } from '../scene-doc/schema';
import type { ApiClient } from './api-client';

/**
 * Where a scene document is kept.
 *
 * Parsed on the way in, wherever it came from: a document written by an older
 * build is rejected rather than half-loaded, which is what keeps the editor and
 * the API from drifting apart (devplan §13). The service stores it as written
 * and checks only its envelope, so this is the one place its shape is enforced.
 */
export interface DocumentStore {
  load: () => Promise<SceneDoc | undefined>;
  save: (doc: SceneDoc) => Promise<void>;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The editor takes its scene id from the URL, so it is the one place that meets
 * ids the service never issued. A scene it created is a UUID; anything else is
 * a scratch draft.
 */
export const isStoredSceneId = (sceneId: string): boolean => UUID.test(sceneId);

const storageKey = (sceneId: string) => `device-verse:scene-doc:${sceneId}`;

const parse = (raw: unknown): SceneDoc | undefined => {
  const parsed = SceneDocSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
};

/**
 * An id the service never issued — a scratch scene opened by URL — stays in the
 * tab it was made in. It is the phase-1 sandbox, kept so a bookmark still
 * works, and nothing else reads it.
 */
const localStore = (sceneId: string): DocumentStore => ({
  load: () => {
    const stored = window.localStorage.getItem(storageKey(sceneId));
    if (!stored) {
      return Promise.resolve(undefined);
    }
    try {
      return Promise.resolve(parse(JSON.parse(stored)));
    } catch {
      return Promise.resolve(undefined);
    }
  },
  save: (doc) => {
    window.localStorage.setItem(storageKey(sceneId), JSON.stringify(doc));
    return Promise.resolve();
  },
});

const apiStore = (worldId: string, sceneId: string, api: ApiClient): DocumentStore => ({
  load: async () => parse(await api.getSceneDocument(worldId, sceneId)),
  save: (doc) => api.putSceneDocument(worldId, sceneId, doc),
});

export const documentStore = (worldId: string, sceneId: string, api: ApiClient): DocumentStore =>
  isStoredSceneId(sceneId) ? apiStore(worldId, sceneId, api) : localStore(sceneId);
